import React, { useState, useEffect } from 'react';
import { getDataWithComp, parseCurrency, useEscapeClose } from '../../../utils/SamuiUtils';
import { useModalKeyboardNavigation, getKeyboardInstructions } from '../../../utils/ModalKeyboardNavigation';

const EmployeeModal = ({ showEmpModal, handleEmpClose, empDataList, onRowSelectEmp }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredEmpDataList, setFilteredEmpDataList] = useState(empDataList);
    const [departmentList, setDepartmentList] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedRowIndex, setSelectedRowIndex] = useState(0); // เพิ่ม state สำหรับเลือกแถว

    useEscapeClose(showEmpModal, handleEmpClose);

    // ใช้ Modal Keyboard Navigation Utility
    useModalKeyboardNavigation(
        showEmpModal,
        filteredEmpDataList,
        selectedRowIndex,
        setSelectedRowIndex,
        onRowSelectEmp,
        handleEmpClose,
        {
            additionalKeys: ['INPUT', 'SELECT'] // เพื่อให้รองรับ dropdown ด้วย
        }
    );

    useEffect(() => {
        setFilteredEmpDataList(
            empDataList.filter(emp =>
                (emp.Emp_Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    emp.Emp_Name.toLowerCase().includes(searchTerm.toLowerCase())) &&
                (!selectedDepartment || emp.Emp_Dept === selectedDepartment)
            )
        );
        setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อข้อมูลเปลี่ยน
    }, [searchTerm, empDataList, selectedDepartment]);

    useEffect(() => {
        if (showEmpModal) {
            setSearchTerm('');
            setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อเปิด modal
            getDepartment();

            if (window.location.pathname === '/technical-equip-form') {
                setSelectedDepartment(9);
            } else {
                setSelectedDepartment('');
            }
        }
    }, [showEmpModal]);

    // เพิ่ม keyboard navigation สำหรับ modal
    useEffect(() => {
        const handleKeyDown = (event) => {
            // ทำงานเฉพาะเมื่อ modal เปิดอยู่
            if (!showEmpModal) return;

            // ป้องกันการทำงานถ้า focus อยู่ใน input search หรือ select
            if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                // ยกเว้น Enter, ESC, F5 และ ArrowDown ที่ยังให้ทำงานได้ใน input/select
                if (event.key !== 'Enter' && event.key !== 'Escape' && event.key !== 'F5' && event.key !== 'ArrowDown') {
                    return;
                }
            }

            if (filteredEmpDataList.length === 0) return;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex > 0 ? prevIndex - 1 : filteredEmpDataList.length - 1;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        scrollToRow(newIndex);
                        return newIndex;
                    });
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    // ถ้าอยู่ใน input/select และกดลูกศรลง ให้ focus ไปที่ตารางและเลือกแถวแรก
                    if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                        setSelectedRowIndex(0);
                        scrollToRow(0);
                        // เอา focus ออกจาก input/select
                        document.activeElement.blur();
                        return;
                    }

                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex < filteredEmpDataList.length - 1 ? prevIndex + 1 : 0;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        scrollToRow(newIndex);
                        return newIndex;
                    });
                    break;

                case 'Enter':
                    event.preventDefault();
                    if (filteredEmpDataList[selectedRowIndex]) {
                        onRowSelectEmp(filteredEmpDataList[selectedRowIndex]);
                    }
                    break;

                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                    console.log('ESC pressed in EmployeeModal, closing modal');
                    handleEmpClose();
                    return false;

                case 'F5':
                    event.preventDefault();
                    const searchInput = document.querySelector('.modal.show input[type="text"]');

                    // ถ้า focus อยู่ใน input อยู่แล้ว ให้เอา focus ออกเพื่อใช้ลูกศรได้
                    if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                        document.activeElement.blur();
                        setSelectedRowIndex(0); // เลือกแถวแรก
                        scrollToRow(0);
                    } else {
                        // ถ้าไม่ได้ focus ใน input ให้ focus ช่องค้นหา
                        if (searchInput) {
                            searchInput.focus();
                        }
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [showEmpModal, filteredEmpDataList, selectedRowIndex, handleEmpClose]);

    // ฟังก์ชันสำหรับเลื่อน scroll ไปยังแถวที่เลือก
    const scrollToRow = (rowIndex) => {
        setTimeout(() => {
            const tableContainer = document.querySelector('.modal.show .table-responsive');
            const selectedRow = document.querySelector(`.modal.show tbody tr:nth-child(${rowIndex + 1})`);

            if (tableContainer && selectedRow) {
                // คำนวณตำแหน่งที่ต้องเลื่อน
                const rowTop = selectedRow.offsetTop;
                const rowHeight = selectedRow.offsetHeight;
                const containerHeight = tableContainer.clientHeight;
                const scrollTop = tableContainer.scrollTop;

                // ตรวจสอบว่าแถวอยู่นอกเขตมองเห็นหรือไม่
                if (rowTop < scrollTop) {
                    // แถวอยู่เหนือเขตมองเห็น - เลื่อนขึ้น
                    tableContainer.scrollTop = Math.max(0, rowTop - 20); // เผื่อ padding
                } else if (rowTop + rowHeight > scrollTop + containerHeight) {
                    // แถวอยู่ใต้เขตมองเห็น - เลื่อนลง
                    tableContainer.scrollTop = rowTop - containerHeight + rowHeight + 20; // เผื่อ padding
                }
            }
        }, 10);
    };

    const getDepartment = async () => {
        const department = await getDataWithComp('Tb_Set_Dept', `AND Dept_Status = 'Y' ORDER BY Dept_Name ASC`);
        if (department && department.length > 0) {
            setDepartmentList(department);
        }
    };

    return (
        <>
            <div className={`modal ${showEmpModal ? 'show' : ''}`} style={{ display: showEmpModal ? 'block' : 'none' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">รายชื่อพนักงาน</h5>
                            <button type="button" className="close" onClick={handleEmpClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-8">
                                    <div className="form-group">
                                        <label>ค้นหาพนักงาน</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="ค้นหา รหัสพนักงาน หรือ ชื่อพนักงาน"
                                            value={searchTerm || ''}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="form-group">
                                        <label>เลือกแผนก</label>
                                        <select
                                            className="form-select form-control input-spacing"
                                            onChange={(e) => setSelectedDepartment(parseCurrency(e.target.value))}
                                            name="docType"
                                            value={selectedDepartment}
                                        >
                                            <option value={''}>กรุณาเลือกแผนก</option>
                                            {departmentList.map((dept) => (
                                                <option key={dept.Dept_Id} value={dept.Dept_Id}>
                                                    {dept.Dept_Name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            <th className="text-center" style={{ width: '15%' }}>รหัสพนักงาน</th>
                                            <th className="text-center" style={{ width: '55%' }}>ชื่อพนักงาน</th>
                                            <th className="text-center" style={{ width: '30%' }}>แผนก</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEmpDataList && filteredEmpDataList.length > 0 ? (
                                            filteredEmpDataList.map((emp, index) => (
                                                <tr
                                                    key={emp.Emp_Id || index + 1}
                                                    onClick={() => {
                                                        setSelectedRowIndex(index); // อัปเดต selectedRowIndex เมื่อคลิก
                                                        onRowSelectEmp(emp);
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: index === selectedRowIndex
                                                            ? "rgb(105, 188, 216)"
                                                            : "transparent",
                                                        transition: "background-color 0.3s ease",
                                                    }}>
                                                    <td className="text-center">{emp.Emp_Code}</td>
                                                    <td style={{ textAlign: 'left' }}>{emp.Emp_Name}</td>
                                                    <td style={{ textAlign: 'left' }}>{emp.Dept_Name}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10">
                                                    <center>
                                                        <h5>ไม่พบข้อมูล</h5>
                                                    </center>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="text-left" style={{ fontSize: "12px", color: "#666", marginRight: "auto" }}>
                                คีย์บอร์ด: F5 (ค้นหา) | ↑↓ (เลือกพนักงาน) | Enter (ยืนยัน) | ESC (ปิด)
                            </div>
                            <button className="btn btn-secondary" onClick={handleEmpClose}>
                                ปิด (ESC)
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* Overlay */}
            {showEmpModal && <div className="modal-backdrop fade show"></div>}
        </>
    );
};

export default EmployeeModal;
